import type { Person, PersonCompany } from 'types/accessManager.type';
import { PRODUCT_LABELS, roleLabel } from 'pages/AccessManager/products';
import type { Product } from 'types/accessManager.type';

export type TTSFilter = 'all' | 'with' | 'without';
export type SortKey = 'created' | 'email' | 'tts';
export type SortDirection = 'asc' | 'desc';

// The people a filter keeps: by email or name, and by whether TTS lets them in.
export const filterPeople = (people: Person[], query: string, tts: TTSFilter): Person[] => {
    const q = query.trim().toLowerCase();
    return people.filter((p) => {
        if (tts === 'with' && !p.tts) return false;
        if (tts === 'without' && p.tts) return false;
        return !q || p.email.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
    });
};

const compare = (a: Person, b: Person, key: SortKey): number => {
    switch (key) {
        case 'email':
            return a.email.localeCompare(b.email);
        case 'tts':
            // With TTS first in ascending order; the newest first among equals.
            return Number(b.tts) - Number(a.tts) || b.created_at.localeCompare(a.created_at);
        default:
            return a.created_at.localeCompare(b.created_at);
    }
};

export const sortPeople = (people: Person[], key: SortKey, direction: SortDirection): Person[] => {
    const sorted = [...people].sort((a, b) => compare(a, b, key));
    return direction === 'desc' ? sorted.reverse() : sorted;
};

// "Acme — Platform: Agent, owner".
export const companyLine = (c: PersonCompany): string => {
    const roles = (Object.entries(c.roles) as [Product, string[]][])
        .filter(([, keys]) => keys && keys.length > 0)
        .map(([product, keys]) => `${PRODUCT_LABELS[product] ?? product}: ${keys.map(roleLabel).join(', ')}`);
    if (c.owner) roles.push('owner');
    return roles.length ? `${c.name} — ${roles.join('; ')}` : c.name;
};

export const isDisabled = (p: Person): boolean => p.status !== 'ACTIVE';

// What the TTS column says: On, Off (switched off or never switched on), the
// status of an entitlement that keeps them out (Suspended, Past due), or
// Disabled — a disabled person is let in nowhere, whatever their account has.
export const ttsLabel = (p: Person): string => {
    if (isDisabled(p)) return 'Disabled';
    if (p.tts) return 'On';
    if (!p.personal_account_id) return 'No personal account';
    const status = p.products.tts?.status ?? 'OFF';
    return status.charAt(0) + status.slice(1).toLowerCase().replace('_', ' ');
};

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// One page of the list, and how many pages there are; a page past the end
// (the list got shorter) shows the last one.
export const paginate = <T>(items: T[], page: number, rowsPerPage: number) => {
    const totalPages = Math.max(1, Math.ceil(items.length / rowsPerPage));
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const start = (currentPage - 1) * rowsPerPage;
    return { rows: items.slice(start, start + rowsPerPage), currentPage, totalPages };
};
